/**
 * eSign — Envelope editor. Place typed/signature/choice fields on the PDF, one set
 * per recipient, configure each field's properties, then Save & Send.
 * Fields use percent coordinates so placement survives zoom/resize.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  PenLine, Type, Calendar, CheckSquare, User, Mail, Hash, Phone, Building2,
  ChevronDown, Circle, StickyNote, Check, X, Minus, ArrowLeft, Send, Save, Trash2, Plus, Loader2,
  Sparkles, AlertTriangle,
} from 'lucide-react';
import esignApi from '../../../api/esign';
import PdfCanvas from '../components/PdfCanvas';
import { DOC_CANVAS } from '../../../components/ui/documentCanvas';

// type -> {label, icon, default size %}. Mirrors eSign's field catalogue.
const FIELD_TYPES = [
  { type: 'signature', label: 'Signature', icon: PenLine, w: 22, h: 6 },
  { type: 'initial', label: 'Initial', icon: PenLine, w: 12, h: 6 },
  { type: 'date', label: 'Date', icon: Calendar, w: 18, h: 4 },
  { type: 'name', label: 'Name', icon: User, w: 20, h: 4 },
  { type: 'email', label: 'Email', icon: Mail, w: 22, h: 4 },
  { type: 'mobile', label: 'Mobile', icon: Phone, w: 18, h: 4 },
  { type: 'text', label: 'Text', icon: Type, w: 20, h: 4 },
  { type: 'number', label: 'Number', icon: Hash, w: 14, h: 4 },
  { type: 'company', label: 'Company', icon: Building2, w: 20, h: 4 },
  { type: 'title', label: 'Title', icon: Type, w: 18, h: 4 },
  { type: 'checkbox', label: 'Checkbox', icon: CheckSquare, w: 3, h: 2.4 },
  { type: 'dropdown', label: 'Dropdown', icon: ChevronDown, w: 20, h: 4 },
  { type: 'radio', label: 'Radio', icon: Circle, w: 3, h: 2.4 },
  { type: 'note', label: 'Note', icon: StickyNote, w: 24, h: 8 },
  { type: 'approve', label: 'Approve', icon: Check, w: 16, h: 5 },
  { type: 'decline', label: 'Decline', icon: X, w: 16, h: 5 },
  { type: 'line', label: 'Line', icon: Minus, w: 25, h: 2 },
];
const TYPE_META = Object.fromEntries(FIELD_TYPES.map((f) => [f.type, f]));

// Which config controls each type exposes (parity with eSign's property panel).
const HAS_DEFAULT = ['text', 'name', 'email', 'mobile', 'company', 'title', 'number', 'date', 'stamp'];
// Fields whose default value can be inline-edited by DOUBLE-CLICKING the placed field (eSign parity).
const DATA_INPUT = ['text', 'date', 'name', 'email', 'mobile', 'company', 'title', 'number'];
const HAS_READONLY = ['text', 'number', 'mobile', 'company', 'title', 'checkbox'];
const HAS_PLACEHOLDER = ['text', 'number'];
const HAS_OPTIONS = ['dropdown', 'radio'];
const NON_REQUIRED_TYPES = ['note', 'line', 'approve', 'decline'];

const RECIPIENT_COLORS = ['#0d9488', '#7c3aed', '#db2777', '#ea580c', '#2563eb'];

// Field size limits (percent of page) + the 8 resize handles.
const MIN_W = 3, MAX_W = 95, MIN_H = 2, MAX_H = 60;
const HANDLES = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
const HANDLE_STYLE = {
  nw: { left: -4, top: -4, cursor: 'nwse-resize' },
  n: { left: '50%', top: -4, marginLeft: -4, cursor: 'ns-resize' },
  ne: { right: -4, top: -4, cursor: 'nesw-resize' },
  e: { right: -4, top: '50%', marginTop: -4, cursor: 'ew-resize' },
  se: { right: -4, bottom: -4, cursor: 'nwse-resize' },
  s: { left: '50%', bottom: -4, marginLeft: -4, cursor: 'ns-resize' },
  sw: { left: -4, bottom: -4, cursor: 'nesw-resize' },
  w: { left: -4, top: '50%', marginTop: -4, cursor: 'ew-resize' },
};

export default function EnvelopeEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const autoFilled = useRef(false); // ?autofill=1 runs the AI fill once, on arrival
  const [envelope, setEnvelope] = useState(null);
  const [fields, setFields] = useState([]);
  // Set when the uploaded contract isn't the LIV/REIV standard form: the auto-fill
  // deliberately places no signing fields, so the agent must add them by hand.
  const [nonStandardNotice, setNonStandardNotice] = useState(false);
  const [activeSR, setActiveSR] = useState(null);
  const [activeType, setActiveType] = useState('signature');
  const [selectedKey, setSelectedKey] = useState(null);
  const [editingKey, setEditingKey] = useState(null); // field being inline-edited (double-click)
  const [pageSize, setPageSize] = useState(null);
  const [pdfSrc, setPdfSrc] = useState(null);
  const [pdfError, setPdfError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const drag = useRef(null);
  const resize = useRef(null);
  const moved = useRef(false);          // did the pointer actually move during drag/resize?
  const suppressClick = useRef(false);  // swallow the click the browser fires after a drag/resize
  const lineDrawRef = useRef(null);     // active line being drawn (Paint-style)
  const [linePreview, setLinePreview] = useState(null); // rubber-band line while drawing

  const load = useCallback(async () => {
    try {
      const { data } = await esignApi.getEnvelope(id);
      // Non-editable docs belong on the read-only detail page, not the field editor.
      if (data.status === 'completed' || data.status === 'voided' || data.status === 'deleted') {
        navigate(`/esign/document/${id}`, { replace: true });
        return;
      }
      setEnvelope(data);
      const flat = [];
      (data.sign_requests || []).forEach((sr) => {
        (sr.fields || []).forEach((f) => {
          flat.push({ key: `${sr.id}-${f.field_id}`, sign_request_id: sr.id, ...f });
        });
      });
      setFields(flat);
      const firstSigner = (data.sign_requests || [])[0];
      setActiveSR(firstSigner ? firstSigner.id : null);
    } catch {
      toast.error('Could not load document.');
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  // Load the PDF *through the API* as a blob (same-origin, HTTPS) rather than letting
  // react-pdf fetch the raw S3 URL — which the browser blocks on the https prod page
  // (mixed-content / S3 CORS), surfacing as "Couldn't display the document."
  // Gated on the envelope's file URL so it fires ONCE with a fallback ready.
  const envOriginalUrl = envelope?.original_url;
  useEffect(() => {
    if (!envOriginalUrl) return undefined;
    let cancelled = false;
    let objectUrl = null;
    setPdfError(false);
    (async () => {
      try {
        const { data } = await esignApi.fileBlob(id);
        if (cancelled) return;
        objectUrl = URL.createObjectURL(data);
        setPdfSrc(objectUrl);
      } catch {
        // Stream endpoint unavailable (e.g. backend not yet redeployed) — fall back to
        // the direct file URL so the viewer still works wherever the browser allows it.
        if (!cancelled) setPdfSrc(envOriginalUrl);
      }
    })();
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [id, envOriginalUrl]);

  const srColor = (srId) => {
    const idx = (envelope?.sign_requests || []).findIndex((s) => s.id === srId);
    return RECIPIENT_COLORS[idx % RECIPIENT_COLORS.length] || '#0d9488';
  };

  const addFieldAt = (pageNumber, xPct, yPct) => {
    if (!activeSR) return toast.error('Add a signer first.');
    const def = TYPE_META[activeType];
    const fid = `f_${Date.now()}_${fields.length}`;
    const key = `new-${fid}`;
    const base = {
      key, sign_request_id: activeSR, field_id: fid, type: activeType,
      page: pageNumber, x_pct: xPct, y_pct: yPct, w_pct: def.w, h_pct: def.h,
      required: !NON_REQUIRED_TYPES.includes(activeType),
      default_value: '', placeholder: '', caption: '', name_format: '',
      group_name: '', note_content: '', read_only: false, options: [],
      min_value: null, max_value: null, decimal_places: 0,
    };
    if (activeType === 'name') base.name_format = 'Full Name';
    if (HAS_OPTIONS.includes(activeType)) base.options = [{ label: 'Option 1', value: 'Option 1' }];
    if (activeType === 'line') {
      base.x1_pct = xPct; base.y1_pct = yPct + def.h / 2;
      base.x2_pct = xPct + def.w; base.y2_pct = yPct + def.h / 2;
    }
    setFields((fs) => [...fs, base]);
    setSelectedKey(key);
  };

  const onPageClick = (pageNumber, width, height) => (e) => {
    // Ignore the synthetic click the browser fires right after a drag/resize.
    if (suppressClick.current) { suppressClick.current = false; return; }
    // Clicked on (or inside) an existing field/handle/icon — select it, don't place a new one.
    if (e.target.closest && e.target.closest('[data-field]')) return;
    // If a field is selected / being edited, an empty-space click just DESELECTS — never places.
    if (selectedKey || editingKey) { setSelectedKey(null); setEditingKey(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const xPct = ((e.clientX - rect.left) / width) * 100;
    const yPct = ((e.clientY - rect.top) / height) * 100;
    addFieldAt(pageNumber, xPct, yPct);
  };

  // ---- drag (from any grab point) ----
  const startDrag = (fieldKey, width, height) => (e) => {
    e.stopPropagation();
    e.preventDefault();
    setSelectedKey(fieldKey);
    const fieldEl = e.currentTarget;
    const overlay = fieldEl.parentElement;
    const fieldRect = fieldEl.getBoundingClientRect();
    const grabX = e.clientX - fieldRect.left;
    const grabY = e.clientY - fieldRect.top;
    const field = fields.find((f) => f.key === fieldKey);
    drag.current = {
      fieldKey, overlay, width, height, grabX, grabY,
      wPct: field?.w_pct || 0, hPct: field?.h_pct || 0,
    };
    window.addEventListener('mousemove', onDrag);
    window.addEventListener('mouseup', endDrag);
  };
  const onDrag = (e) => {
    if (!drag.current) return;
    const { fieldKey, overlay, width, height, grabX, grabY, wPct, hPct } = drag.current;
    const rect = overlay.getBoundingClientRect();
    let xPct = ((e.clientX - rect.left - grabX) / width) * 100;
    let yPct = ((e.clientY - rect.top - grabY) / height) * 100;
    xPct = Math.max(0, Math.min(100 - wPct, xPct));
    yPct = Math.max(0, Math.min(100 - hPct, yPct));
    moved.current = true;
    setFields((fs) => fs.map((f) => (f.key === fieldKey ? { ...f, x_pct: xPct, y_pct: yPct } : f)));
  };
  const endDrag = () => {
    drag.current = null;
    if (moved.current) suppressClick.current = true;
    moved.current = false;
    window.removeEventListener('mousemove', onDrag);
    window.removeEventListener('mouseup', endDrag);
  };

  // ---- resize (8 handles) ----
  const startResize = (fieldKey, handle, width, height) => (e) => {
    e.stopPropagation();
    e.preventDefault();
    setSelectedKey(fieldKey);
    const field = fields.find((f) => f.key === fieldKey);
    resize.current = {
      fieldKey, handle, width, height,
      xPct: field.x_pct, yPct: field.y_pct, wPct: field.w_pct, hPct: field.h_pct,
      clientX: e.clientX, clientY: e.clientY,
    };
    window.addEventListener('mousemove', onResize);
    window.addEventListener('mouseup', endResize);
  };
  const onResize = (e) => {
    if (!resize.current) return;
    const { fieldKey, handle, width, height } = resize.current;
    const dxPct = ((e.clientX - resize.current.clientX) / width) * 100;
    const dyPct = ((e.clientY - resize.current.clientY) / height) * 100;
    let { xPct, yPct, wPct, hPct } = resize.current;
    if (handle.includes('e')) wPct = Math.min(MAX_W, Math.max(MIN_W, wPct + dxPct));
    // West/north move the origin as they resize; bound dw/dh so the field can't
    // shrink below the min, grow past the max, or push its edge off the page.
    if (handle.includes('w')) { const dw = Math.min(wPct - MIN_W, Math.max(dxPct, wPct - MAX_W, -xPct)); wPct -= dw; xPct += dw; }
    if (handle.includes('s')) hPct = Math.min(MAX_H, Math.max(MIN_H, hPct + dyPct));
    if (handle.includes('n')) { const dh = Math.min(hPct - MIN_H, Math.max(dyPct, hPct - MAX_H, -yPct)); hPct -= dh; yPct += dh; }
    moved.current = true;
    setFields((fs) => fs.map((f) => (f.key === fieldKey
      ? { ...f, x_pct: xPct, y_pct: yPct, w_pct: wPct, h_pct: hPct } : f)));
    resize.current = { ...resize.current, xPct, yPct, wPct, hPct, clientX: e.clientX, clientY: e.clientY };
  };
  const endResize = () => {
    resize.current = null;
    if (moved.current) suppressClick.current = true;
    moved.current = false;
    window.removeEventListener('mousemove', onResize);
    window.removeEventListener('mouseup', endResize);
  };

  // ---- line drawing (Paint-style: click-drag to draw an actual line at any angle) ----
  const onPageMouseDown = (pageNumber, width, height) => (e) => {
    if (locked || activeType !== 'line') return;
    if (e.target.closest && e.target.closest('[data-field]')) return; // don't start on a field
    e.preventDefault();
    const overlayEl = e.currentTarget;
    const rect = overlayEl.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / width) * 100));
    const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / height) * 100));
    lineDrawRef.current = { pageNumber, width, height, overlayEl, x1: x, y1: y, x2: x, y2: y };
    setLinePreview({ pageNumber, x1: x, y1: y, x2: x, y2: y });
    window.addEventListener('mousemove', onLineMove);
    window.addEventListener('mouseup', onLineUp);
  };
  const onLineMove = (e) => {
    const d = lineDrawRef.current;
    if (!d) return;
    const rect = d.overlayEl.getBoundingClientRect();
    d.x2 = Math.max(0, Math.min(100, ((e.clientX - rect.left) / d.width) * 100));
    d.y2 = Math.max(0, Math.min(100, ((e.clientY - rect.top) / d.height) * 100));
    setLinePreview({ pageNumber: d.pageNumber, x1: d.x1, y1: d.y1, x2: d.x2, y2: d.y2 });
  };
  const onLineUp = () => {
    window.removeEventListener('mousemove', onLineMove);
    window.removeEventListener('mouseup', onLineUp);
    const d = lineDrawRef.current;
    lineDrawRef.current = null;
    setLinePreview(null);
    if (!d) return;
    suppressClick.current = true; // don't let the click place/deselect
    const len = Math.hypot(d.x2 - d.x1, d.y2 - d.y1);
    if (len < 1) return; // too short — treat as a no-op, keep the Line tool active
    if (!activeSR) { toast.error('Add a signer first.'); return; }
    const fid = `f_${Date.now()}_${fields.length}`;
    const key = `new-${fid}`;
    setFields((fs) => [...fs, {
      key, sign_request_id: activeSR, field_id: fid, type: 'line', page: d.pageNumber,
      x_pct: Math.min(d.x1, d.x2), y_pct: Math.min(d.y1, d.y2),
      w_pct: Math.max(0.5, Math.abs(d.x2 - d.x1)), h_pct: Math.max(0.5, Math.abs(d.y2 - d.y1)),
      x1_pct: d.x1, y1_pct: d.y1, x2_pct: d.x2, y2_pct: d.y2,
      required: false, default_value: '', placeholder: '', caption: '', name_format: '',
      group_name: '', note_content: '', read_only: false, options: [],
      min_value: null, max_value: null, decimal_places: 0,
    }]);
    setSelectedKey(key);
  };

  const removeField = (key) => {
    setFields((fs) => fs.filter((f) => f.key !== key));
    if (selectedKey === key) setSelectedKey(null);
  };

  // Delete/Backspace removes the selected field, matching every other design
  // tool (CG, 2026-08-13: "Cant use the delete button on the keyboard to
  // delete fields"). Skipped while a field is being inline-edited or focus is
  // in a text input/textarea (the recipient/message/property forms), so
  // Backspace still edits text as normal instead of deleting the field.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      if (!selectedKey || editingKey) return;
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;
      e.preventDefault();
      removeField(selectedKey);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey, editingKey]);
  const updateSelected = (patch) =>
    setFields((fs) => fs.map((f) => (f.key === selectedKey ? { ...f, ...patch } : f)));
  const updateField = (key, patch) =>
    setFields((fs) => fs.map((f) => (f.key === key ? { ...f, ...patch } : f)));

  // `list` overrides the fields in state, for a caller that has just built them
  // and cannot wait a render for setFields to land (the offer auto-fill below).
  const save = async ({ silent, list } = {}) => {
    const toSave = list || fields;
    setSaving(true);
    try {
      const assignments = (envelope.sign_requests || []).map((sr) => ({
        sign_request_id: sr.id,
        fields: toSave.filter((f) => f.sign_request_id === sr.id).map(serializeField),
      }));
      await esignApi.saveFields(id, {
        page1_render_width: pageSize?.width,
        page1_render_height: pageSize?.height,
        assignments,
      });
      if (!silent) toast.success('Fields saved.');
      return true;
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.error || 'Save failed.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Auto-save placed fields before leaving, so they persist when the doc is reopened.
  const handleBack = async () => {
    if (!locked && fields.length) await save({ silent: true });
    // A Contract of Sale is opened from its buyer's-offer questions form via
    // "Continue to contract editor", so Back here should return to those questions
    // rather than the documents list (DOMA 13213486581). Other envelopes still go
    // back to the agreements list.
    if (envelope?.doc_kind === 'contract_of_sale') {
      navigate(`/esign/contract-of-sale/${id}`);
    } else {
      navigate('/esign/agreements');
    }
  };

  const saveAndSend = async () => {
    if (sending) return;
    setSending(true);
    await save({ silent: true });
    try {
      const { data } = await esignApi.send(id);
      if (data.emails_failed) {
        toast.error(`Sent, but ${data.emails_failed} email(s) failed: ${data.warning || ''}`);
      } else {
        toast.success(`Sent to ${data.emails_sent ?? 0} recipient(s).`);
      }
      navigate('/esign/agreements');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Send failed.');
    } finally {
      setSending(false);
    }
  };

  // ---- AI field detection (Contract of Sale) ----
  // Detects blanks + signature blocks on the PDF and drops them onto the ACTIVE
  // recipient for the agent to review/reassign. Coordinates come from the PDF
  // itself, so they land on the real fill lines.
  const runAnalyze = async () => {
    if (analyzing) return;
    if (!activeSR) return toast.error('Add a signer first, then run detection.');
    if (fields.length && !window.confirm(
      'Add AI-detected fields to the selected recipient? Existing fields are kept — you can delete any duplicates.')) return;
    setAnalyzing(true);
    // Contract of Sale envelopes: run the AI-fill that also injects the agent's
    // buyer-sheet answers (+ vendor details from the authority) into the detected
    // fields. Everything else uses plain AI field-detection.
    const isCos = envelope?.doc_kind === 'contract_of_sale';
    const t = toast.loading(isCos ? 'Auto-filling contract…' : 'Analysing document…');
    try {
      const { data } = isCos ? await esignApi.cosAutofill(id) : await esignApi.analyzeContract(id);
      const layout = isCos ? (data.layout || {}) : data;
      const detected = layout.fields || [];
      // A non-standard lawyer contract: the BE recognised it isn't the LIV/REIV
      // standard form, so it deliberately placed no signing fields (nothing
      // scatters). Flag it so the agent places them by hand (ticket 12525266778).
      const nonStandard = isCos && !!data.non_standard;
      // Signature/date fields were actually placed (by geometry OR re-applied
      // from a previously-signed look-alike contract - "layout memory", ticket
      // 12525266778). When they were, we must NOT tell the agent to add signing
      // fields by hand: that would be wrong and invites duplicate boxes.
      const signingPlaced = isCos && !!data.signing_fields_placed;
      const learned = (isCos && data.learned) || 0;
      // Execution fields the model placed directly from the contract pages when
      // there was no learned precedent to reuse (ticket 12525266778).
      const aiSignatures = (isCos && data.ai_signatures) || 0;
      setNonStandardNotice(nonStandard && !signingPlaced);
      if (!detected.length) {
        if (nonStandard) {
          toast("This doesn't look like the standard contract form, so nothing was auto-placed. Add the buyer details and signing fields manually.",
            { id: t, icon: '⚠️', duration: 9000 });
        } else {
          toast.error('No fillable fields detected.', { id: t });
        }
        return;
      }
      const now = Date.now();
      const mapped = detected.map((f, i) => {
        const type = TYPE_META[f.type] ? f.type : 'text';
        const fid = `ai_${now}_${i}`;
        return {
          key: `new-${fid}`, sign_request_id: activeSR, field_id: fid, type,
          page: f.page || 1,
          x_pct: f.x_pct, y_pct: f.y_pct, w_pct: f.w_pct, h_pct: f.h_pct,
          required: !!f.required,
          // COS pre-fills matched fields from the buyer sheet; keep those values.
          default_value: isCos ? (f.default_value || '') : '',
          placeholder: '', caption: '',
          name_format: type === 'name' ? 'Full Name' : '',
          group_name: '', note_content: '', read_only: false, options: [],
          min_value: null, max_value: null, decimal_places: 0,
          // Carry the AI's semantic label + party hint through to the backend.
          label: f.label || (f.signer && f.signer !== 'other' ? `${f.signer}_${f.type}` : ''),
          // Detection source/confidence: not sent to the backend (not an
          // accepted signing-fields key), just kept so the editor can flag
          // low-confidence placements (an AI vision guess, not measured PDF
          // geometry) for the agent to double-check before sending.
          confidence: f.confidence || '',
        };
      });
      setFields((fs) => [...fs, ...mapped]);
      // Part B: on a non-standard precedent the BE AI-matches the buyer sheet
      // onto the contract's own blanks (high-confidence only). Those come back
      // dashed for review; call them out so the agent checks each one.
      const aiMatched = data.ai_matched || 0;
      const matchedNote = aiMatched
        ? ` We also pre-filled ${aiMatched} detail(s) the AI matched to your entries - dashed for review, please check each one.`
        : '';
      if (isCos && learned) {
        // Layout memory: signature positions came from a previously-signed
        // look-alike contract. They're dashed (low-confidence) so the agent
        // verifies each before sending.
        toast(`Placed ${learned} signature field(s) learned from a similar contract you've signed before - these are dashed, please verify each is in the right spot. Pre-filled ${data.filled || 0} detail(s) from your buyer sheet.${matchedNote}`,
          { id: t, icon: '✅', duration: 9000 });
      } else if (isCos && aiSignatures) {
        // AI placed the execution fields directly from the contract pages (no
        // prior contract to learn from). Dashed for review.
        toast(`AI placed ${aiSignatures} signature field(s) by reading this contract's signing page - these are dashed, please verify each is in the right spot before sending. Pre-filled ${data.filled || 0} detail(s) from your buyer sheet.${matchedNote}`,
          { id: t, icon: '✅', duration: 9000 });
      } else if (isCos && nonStandard && !signingPlaced) {
        toast(`This isn't the standard contract form, so we didn't auto-place the signing fields (nothing was scattered). Added ${mapped.length} line(s) we could find as a starting point - please add the signature/date fields and check the details manually.${matchedNote}`,
          { id: t, icon: '⚠️', duration: 9000 });
      } else if (isCos) {
        toast.success(`Detected ${mapped.length} field(s), pre-filled ${data.filled || 0} from your buyer details. Review, assign signers, then save.`, { id: t });
      } else {
        const src = data.ai_used ? 'AI' : 'text detection';
        toast.success(`Added ${mapped.length} field(s) via ${src}. Review, reassign to signers, then save.`, { id: t });
      }
      return mapped;
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Analysis failed.', { id: t });
    } finally {
      setAnalyzing(false);
    }
    return null;
  };

  // Arriving from "Fill Out Contract" on an offer (?autofill=1): run the fill
  // straight away so the contract opens with the buyer's offer details already
  // on it, instead of the agent having to know to press "AI: detect fields".
  // The flag is consumed so a refresh or a trip back here never re-runs it.
  useEffect(() => {
    if (autoFilled.current || searchParams.get('autofill') !== '1') return;
    if (!envelope || !activeSR) return;
    autoFilled.current = true;
    const next = new URLSearchParams(searchParams);
    next.delete('autofill');
    setSearchParams(next, { replace: true });
    // Only on a contract with nothing placed yet - running over existing fields
    // would add a second copy of every one.
    if (fields.length) return;
    // Persist what the fill placed. The agent did not ask for these fields, so
    // they have no reason to expect losing them by leaving any way other than
    // the Back button (which is the only thing that saves on the way out).
    runAnalyze().then((placed) => {
      if (placed?.length) save({ silent: true, list: placed });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envelope, activeSR, searchParams]);

  if (!envelope) return <div className="p-8 text-gray-400">Loading…</div>;
  const locked = envelope.status === 'completed' || envelope.status === 'voided';
  const selected = fields.find((f) => f.key === selectedKey) || null;

  return (
    <div className="flex h-[calc(100vh-4rem)] overflow-x-auto min-w-0">
      {/* Left sidebar: recipients + field palette */}
      <div className="w-52 sm:w-60 border-r bg-white p-4 overflow-y-auto shrink-0">
        <button onClick={handleBack} className="flex items-center gap-1 text-sm text-gray-500 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <h2 className="font-semibold text-gray-900 mb-1 truncate">{envelope.title}</h2>
        <p className="text-xs text-gray-400 mb-4 capitalize">{envelope.status}</p>

        <div className="mb-5">
          <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Recipient</h3>
          {(envelope.sign_requests || []).map((sr) => (
            <button key={sr.id} onClick={() => setActiveSR(sr.id)}
              className={`flex items-center gap-2 w-full text-left px-2 py-1.5 rounded text-sm mb-1 ${activeSR === sr.id ? 'bg-gray-100 font-medium' : ''}`}>
              <span className="w-3 h-3 rounded-full" style={{ background: srColor(sr.id) }} />
              <span className="truncate">{sr.signer_name || sr.signer_email}</span>
            </button>
          ))}
          {(!envelope.sign_requests || !envelope.sign_requests.length) && (
            <p className="text-xs text-gray-400">No signers on this document.</p>
          )}
        </div>

        {!locked && (
          <button onClick={runAnalyze} disabled={analyzing}
            className="w-full flex items-center justify-center gap-2 mb-4 px-3 py-2 rounded-lg border border-violet-300 bg-violet-50 text-violet-700 text-sm font-medium disabled:opacity-50">
            {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {analyzing ? 'Analysing…' : 'AI: detect fields'}
          </button>
        )}

        {nonStandardNotice && (
          <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] leading-snug text-amber-800">
            <p className="font-semibold mb-0.5">Non-standard contract</p>
            This upload isn't the standard contract form, so the signing fields
            were not auto-placed (nothing was scattered). Add the buyer details
            and the signature/date fields manually, then review before sending.
          </div>
        )}

        <div>
          <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Fields</h3>
          <div className="grid grid-cols-2 gap-2">
            {FIELD_TYPES.map((ft) => (
              <button key={ft.type} onClick={() => setActiveType(ft.type)}
                className={`flex flex-col items-center gap-1 py-2 rounded border text-[11px] transition-colors ${activeType === ft.type ? 'border-teal-500 bg-teal-50 text-teal-700 ring-2 ring-teal-200' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                <ft.icon className="w-4 h-4" />
                {ft.label}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-gray-400 mt-3">Drag to move, drag handles to resize, click to edit, Delete/Backspace to remove.</p>
        </div>
      </div>

      {/* Canvas */}
      <div className={`flex-1 min-w-[280px] overflow-y-auto ${DOC_CANVAS} p-4 sm:p-6 pb-24`}>
        {!locked && activeSR && (() => {
          const ActiveIcon = TYPE_META[activeType]?.icon;
          return (
            <div className="sticky top-0 z-10 mb-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-teal-600 text-white text-sm font-medium shadow">
              {ActiveIcon && <ActiveIcon className="w-4 h-4 shrink-0" />}
              Click on the page to place a {TYPE_META[activeType]?.label || 'field'}
            </div>
          );
        })()}
        <PdfCanvas
          fileUrl={pdfSrc}
          loading={!pdfSrc && !pdfError}
          width={780}
          onFirstPageSize={setPageSize}
          renderOverlay={({ pageNumber, width, height }) => (
            <div className="absolute inset-0" style={{ cursor: locked ? 'default' : 'crosshair' }}
                 onMouseDown={locked ? undefined : onPageMouseDown(pageNumber, width, height)}
                 onClick={locked ? undefined : onPageClick(pageNumber, width, height)}>
              {fields.filter((f) => f.page === pageNumber).map((f) => {
                const c = srColor(f.sign_request_id);
                const isRadio = f.type === 'radio';
                const isCheckbox = f.type === 'checkbox';
                const isLine = f.type === 'line';
                const isControl = isRadio || isCheckbox;      // rendered as the actual control
                const editing = editingKey === f.key && DATA_INPUT.includes(f.type);
                const checked = isCheckbox && String(f.default_value).toLowerCase() === 'true';
                // Same rule as the signing page + PDF: text scales to ~60% of the field height.
                const fontPx = Math.max(8, Math.min(((f.h_pct || 0) / 100) * height * 0.6, 40));
                // ...then shrink to fit the field width, exactly like the signing
                // overlay/baked PDF, so a long default value ($790,000) previews
                // in full here instead of clipping (~0.6em/glyph less px-1 pad).
                const previewFontPx = (() => {
                  const len = String(fieldPreview(f) ?? '').length;
                  const fieldW = ((f.w_pct || 0) / 100) * width;
                  if (!fieldW || !len) return fontPx;
                  return Math.max(8, Math.min(fontPx, (fieldW - 6) / (len * 0.6)));
                })();
                // AI vision guessed this placement from a screenshot rather than
                // measuring the PDF's own geometry - worth a visible nudge to
                // double-check it, not just an equal-looking field.
                const lowConfidence = f.confidence === 'low';
                return (
                  <div key={f.key} data-field="1"
                    onMouseDown={locked ? undefined : startDrag(f.key, width, height)}
                    onDoubleClick={(e) => {
                      if (locked) return;
                      // Double-click a checkbox to tick / untick it right on the page,
                      // which is where agents look to mark a box (same as the
                      // "Checked by default" toggle in the property panel). It flips
                      // default_value, previewed live below and baked into the PDF on
                      // send. Other data fields open their inline default-value editor.
                      if (f.type === 'checkbox') {
                        e.stopPropagation();
                        setSelectedKey(f.key);
                        const nowChecked = String(f.default_value).toLowerCase() === 'true';
                        updateField(f.key, { default_value: nowChecked ? '' : 'true' });
                      } else if (DATA_INPUT.includes(f.type)) {
                        e.stopPropagation(); setSelectedKey(f.key); setEditingKey(f.key);
                      }
                    }}
                    title={lowConfidence
                      ? 'AI-guessed placement - please verify this is correct'
                      : f.type === 'checkbox' ? 'Double-click to tick or untick this box'
                      : (DATA_INPUT.includes(f.type) ? 'Double-click to set a default value' : undefined)}
                    className={`absolute border-2 ${lowConfidence ? 'border-dashed' : ''} ${isRadio ? 'rounded-full' : 'rounded'} flex items-center justify-center text-[10px] font-medium select-none group ${selectedKey === f.key ? 'ring-2 ring-offset-1 ring-teal-500' : ''}`}
                    style={{
                      left: `${f.x_pct}%`, top: `${f.y_pct}%`,
                      width: `${f.w_pct}%`, height: `${f.h_pct}%`,
                      borderColor: c,
                      background: (isLine || isControl) ? 'transparent' : `${c}22`,
                      cursor: 'move',
                    }}>
                    {isLine ? (
                      <svg data-field="1" className="absolute inset-0 w-full h-full overflow-visible"
                        viewBox="0 0 100 100" preserveAspectRatio="none">
                        <line
                          x1={f.x1_pct != null ? ((f.x1_pct - f.x_pct) / (f.w_pct || 1)) * 100 : 0}
                          y1={f.y1_pct != null ? ((f.y1_pct - f.y_pct) / (f.h_pct || 1)) * 100 : 50}
                          x2={f.x2_pct != null ? ((f.x2_pct - f.x_pct) / (f.w_pct || 1)) * 100 : 100}
                          y2={f.y2_pct != null ? ((f.y2_pct - f.y_pct) / (f.h_pct || 1)) * 100 : 50}
                          stroke="#111" strokeWidth={2} vectorEffect="non-scaling-stroke" strokeLinecap="round" />
                      </svg>
                    ) : isControl ? (
                      // Checkbox = square (the box); show a tick only if checked-by-default. Radio = circle.
                      checked ? <Check data-field="1" className="w-3 h-3" style={{ color: c }} /> : null
                    ) : editing ? (
                      f.type === 'text' ? (
                        <textarea data-field="1" autoFocus value={f.default_value || ''}
                          onChange={(e) => updateField(f.key, { default_value: e.target.value })}
                          onBlur={() => setEditingKey(null)}
                          onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => { if (e.key === 'Escape') setEditingKey(null); e.stopPropagation(); }}
                          placeholder="Text"
                          style={{ fontSize: 13 }}
                          className="w-full h-full bg-transparent outline-none resize-none px-1 leading-tight text-gray-900 whitespace-pre-wrap break-words" />
                      ) : (
                        <input data-field="1" autoFocus
                          type={(f.type === 'date' || f.type === 'date_dmy') ? 'date' : f.type === 'number' ? 'number' : 'text'}
                          value={f.default_value || ''}
                          onChange={(e) => updateField(f.key, { default_value: e.target.value })}
                          onBlur={() => setEditingKey(null)}
                          onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') setEditingKey(null); e.stopPropagation(); }}
                          placeholder={f.type === 'name' ? (f.name_format || 'Full Name') : (TYPE_META[f.type]?.label || f.type)}
                          style={{ fontSize: fontPx }}
                          className="w-full bg-transparent outline-none px-1 text-gray-900" />
                      )
                    ) : f.type === 'dropdown' ? (
                      <span data-field="1" style={{ color: c, fontSize: previewFontPx }} className="truncate px-1 flex items-center gap-0.5">
                        {f.default_value || 'Select'} <ChevronDown className="w-3 h-3" />
                      </span>
                    ) : f.type === 'text' ? (
                      <div data-field="1" style={{ color: c, fontSize: 13 }}
                        className="w-full h-full px-1 overflow-hidden whitespace-pre-wrap break-words leading-tight flex items-center">
                        {(f.default_value || '').trim() || 'Text'}
                      </div>
                    ) : (
                      <span data-field="1" style={{ color: c, fontSize: previewFontPx }} className="truncate px-1">{fieldPreview(f)}</span>
                    )}

                    {lowConfidence && (
                      <span data-field="1" title="AI-guessed placement - please verify"
                        className="absolute -top-2 -left-2 bg-white border border-amber-400 rounded-full p-0.5 z-10">
                        <AlertTriangle className="w-3 h-3 text-amber-500" />
                      </span>
                    )}
                    {!locked && (
                      <button data-field="1" onClick={(e) => { e.stopPropagation(); removeField(f.key); }}
                        className="absolute -top-2 -right-2 bg-white border rounded-full p-0.5 opacity-0 group-hover:opacity-100 z-10">
                        <Trash2 className="w-3 h-3 text-red-500" />
                      </button>
                    )}
                    {!locked && HANDLES.map((h) => (
                      <span key={h} data-field="1"
                        onMouseDown={startResize(f.key, h, width, height)}
                        className="absolute w-2 h-2 bg-white border rounded-sm opacity-0 group-hover:opacity-100"
                        style={{ ...HANDLE_STYLE[h], borderColor: c }} />
                    ))}
                  </div>
                );
              })}
              {/* live line preview while drawing */}
              {linePreview && linePreview.pageNumber === pageNumber && (
                <svg className="absolute inset-0 w-full h-full pointer-events-none overflow-visible"
                  viewBox="0 0 100 100" preserveAspectRatio="none">
                  <line x1={linePreview.x1} y1={linePreview.y1} x2={linePreview.x2} y2={linePreview.y2}
                    stroke="#0d9488" strokeWidth={2} strokeDasharray="3 2" vectorEffect="non-scaling-stroke" strokeLinecap="round" />
                </svg>
              )}
            </div>
          )}
        />
      </div>

      {/* Right property panel — always reserved (like eSign) so selecting a field never
          reflows or overlaps the document. */}
      {!locked && (
        <div className="w-72 shrink-0 border-l bg-white overflow-y-auto pb-24">
          {selected ? (
            <PropertyPanel field={selected} onChange={updateSelected} onDelete={() => removeField(selected.key)} />
          ) : (
            <div className="p-6 text-sm text-gray-400 text-center mt-16">
              Select a field to edit its properties, or pick a field type and click the document to place it.
            </div>
          )}
        </div>
      )}

      {/* Action footer (eSign-style bar) */}
      {!locked && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t px-4 sm:px-6 py-3 flex items-center justify-end gap-3 z-40">
          <button onClick={() => save()} disabled={saving || sending}
                  className="flex items-center gap-2 text-sm px-4 py-2 rounded-lg border disabled:opacity-50">
            <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save'}
          </button>
          <button onClick={saveAndSend} disabled={saving || sending}
                  className="flex items-center gap-2 text-sm px-5 py-2 rounded-lg bg-black text-white disabled:opacity-50">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {sending ? 'Sending…' : 'Send'}
          </button>
        </div>
      )}
    </div>
  );
}

// ---- helpers ----
function fieldPreview(f) {
  if (f.type === 'note') return f.note_content || 'Note';
  if (f.type === 'line' || f.type === 'checkbox' || f.type === 'radio') return '';
  return (f.default_value || '').trim() || (f.type === 'name' ? (f.name_format || 'Name') : TYPE_META[f.type]?.label || f.type);
}

function serializeField(f) {
  return {
    field_id: f.field_id, type: f.type, page: f.page,
    x_pct: f.x_pct, y_pct: f.y_pct, w_pct: f.w_pct, h_pct: f.h_pct,
    required: !!f.required, read_only: !!f.read_only,
    default_value: f.default_value || '', placeholder: f.placeholder || '',
    caption: f.caption || '', name_format: f.name_format || '',
    group_name: f.group_name || '', note_content: f.note_content || '',
    options: f.options || [], label: f.label || '',
    min_value: f.min_value, max_value: f.max_value, decimal_places: f.decimal_places || 0,
    x1_pct: f.x1_pct, y1_pct: f.y1_pct, x2_pct: f.x2_pct, y2_pct: f.y2_pct,
  };
}

function PropertyPanel({ field, onChange, onDelete }) {
  const set = (key) => (e) => {
    const v = e && e.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e;
    onChange({ [key]: v });
  };
  const setOptions = (opts) => onChange({ options: opts });
  const addOption = () => setOptions([...(field.options || []), { label: `Option ${(field.options?.length || 0) + 1}`, value: `Option ${(field.options?.length || 0) + 1}` }]);
  const updateOption = (i, val) => setOptions(field.options.map((o, idx) => (idx === i ? { label: val, value: val } : o)));
  const removeOption = (i) => setOptions(field.options.filter((_, idx) => idx !== i));

  const Row = ({ label, children }) => (
    <div className="mb-3">
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      {children}
    </div>
  );
  const input = 'w-full border rounded px-2 py-1.5 text-sm';

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold capitalize">{TYPE_META[field.type]?.label || field.type} field</h3>
        <button onClick={onDelete} className="text-red-500"><Trash2 className="w-4 h-4" /></button>
      </div>

      {!NON_REQUIRED_TYPES.includes(field.type) && (
        <label className="flex items-center gap-2 text-sm mb-3">
          <input type="checkbox" checked={!!field.required} disabled={field.read_only} onChange={set('required')} />
          Required field
        </label>
      )}

      {HAS_READONLY.includes(field.type) && (
        <label className="flex items-center gap-2 text-sm mb-3">
          <input type="checkbox" checked={!!field.read_only}
                 onChange={(e) => onChange({ read_only: e.target.checked, required: e.target.checked ? false : field.required })} />
          Read only
        </label>
      )}

      {field.type === 'name' && (
        <Row label="Name format">
          <select className={input} value={field.name_format || 'Full Name'} onChange={set('name_format')}>
            <option>Full Name</option><option>First Name</option><option>Last Name</option>
          </select>
        </Row>
      )}

      {HAS_DEFAULT.includes(field.type) && field.type !== 'date' && (
        <Row label="Default value"><input className={input} value={field.default_value || ''} onChange={set('default_value')} placeholder="Pre-fill with value" /></Row>
      )}
      {field.type === 'date' && (
        <Row label="Default date"><input type="date" className={input} value={field.default_value || ''} onChange={set('default_value')} /></Row>
      )}
      {HAS_PLACEHOLDER.includes(field.type) && (
        <Row label="Placeholder"><input className={input} value={field.placeholder || ''} onChange={set('placeholder')} /></Row>
      )}

      {field.type === 'checkbox' && (
        <>
          <Row label="Caption"><input className={input} value={field.caption || ''} onChange={set('caption')} placeholder="Label next to checkbox" /></Row>
          <label className="flex items-center gap-2 text-sm mb-1">
            <input type="checkbox" checked={String(field.default_value).toLowerCase() === 'true'}
                   onChange={(e) => onChange({ default_value: e.target.checked ? 'true' : '' })} />
            Checked by default
          </label>
          <p className="text-xs text-gray-400 mb-3">Tip: double-click the box on the page to tick or untick it.</p>
        </>
      )}

      {field.type === 'number' && (
        <>
          <Row label="Min value"><input type="number" className={input} value={field.min_value ?? ''} onChange={(e) => onChange({ min_value: e.target.value === '' ? null : Number(e.target.value) })} /></Row>
          <Row label="Max value"><input type="number" className={input} value={field.max_value ?? ''} onChange={(e) => onChange({ max_value: e.target.value === '' ? null : Number(e.target.value) })} /></Row>
          <Row label="Decimal places"><input type="number" min={0} max={10} className={input} value={field.decimal_places ?? 0} onChange={(e) => onChange({ decimal_places: Math.max(0, Math.min(10, Number(e.target.value) || 0)) })} /></Row>
        </>
      )}

      {HAS_OPTIONS.includes(field.type) && (
        <>
          {field.type === 'radio' && (
            <Row label="Group name"><input className={input} value={field.group_name || ''} onChange={set('group_name')} placeholder="Link radio buttons" /></Row>
          )}
          <label className="block text-xs font-medium text-gray-500 mb-1">Options</label>
          {(field.options || []).map((o, i) => (
            <div key={i} className="flex gap-1 mb-1">
              <input className="flex-1 border rounded px-2 py-1 text-sm" value={o.label}
                     onChange={(e) => updateOption(i, e.target.value)} />
              <button onClick={() => removeOption(i)} className="text-gray-400"><X className="w-4 h-4" /></button>
            </div>
          ))}
          <button onClick={addOption} className="flex items-center gap-1 text-xs text-teal-600 mb-3"><Plus className="w-3 h-3" /> Add option</button>
          <Row label="Default option">
            <select className={input} value={field.default_value || ''} onChange={set('default_value')}>
              <option value="">— none —</option>
              {(field.options || []).map((o, i) => <option key={i} value={o.value}>{o.label}</option>)}
            </select>
          </Row>
        </>
      )}

      {field.type === 'note' && (
        <Row label="Note for recipient">
          <textarea className={input} rows={4} value={field.note_content || ''} onChange={set('note_content')}
                    placeholder="Message shown to the recipient. Not written on the document." />
        </Row>
      )}

      <Row label="AI-fill label (optional)">
        <input className={input} value={field.label || ''} onChange={set('label')} placeholder="e.g. buyer_name" />
      </Row>
    </div>
  );
}
